import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import Graficos from './pages/Graficos';
import Reportes from './pages/Reportes';
import Login from './components/Login';
import PanelControl from './pages/PanelControl';
import ConfigReportes from './pages/ConfigReportes';
import { Spin } from 'antd';
import { AuthProvider } from './contexts/AuthContext';
import { RegistrosProvider, useRegistros } from './contexts/RegistrosContext';

// Elimina variables no usadas para limpiar warnings
// drawerWidth y collapsedWidth

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
};

const AppRoutes: React.FC = () => {
  const location = useLocation();
  const isAuthenticated = !!localStorage.getItem('token');
  const isMobile = useIsMobile();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Usar el hook de registros del context SOLO si está autenticado
  const { registros, loading, error, cargarDatos } = useRegistros();

  // Cargar datos al montar si está autenticado
  useEffect(() => {
    if (isAuthenticated && location.pathname !== '/login') {
      cargarDatos();
    }
  }, [isAuthenticated, location.pathname]);

  // Sincroniza el colapso del sidebar con el evento desde Sidebar
  const handleSidebarCollapse = (collapsed: boolean) => setSidebarCollapsed(collapsed);

  // Si está en /login, mostrar solo el login (sin sidebar ni header)
  if (location.pathname === '/login') {
    return <Login />;
  }

  // Si no está autenticado, redirigir a login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        background: 'linear-gradient(to bottom, #34495e, #2c3e50)',
      }}
    >
      {!isMobile && <Sidebar collapsed={sidebarCollapsed} onCollapse={handleSidebarCollapse} />}
      <div
        style={{
          flexGrow: 1,
          width: '100%',
          marginLeft: !isMobile ? (sidebarCollapsed ? 80 : 220) : 0,
          transition: 'margin-left 0.2s cubic-bezier(.4,0,.2,1)',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          minHeight: '100vh',
        }}
      >
        <Header title="Informe de Rondas" />
        <div style={{ padding: isMobile ? 8 : 24, flex: 1 }}>
          {loading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Spin size="large" tip="Cargando..." />
            </div>
          ) : error ? (
            <div style={{ marginBottom: 16, color: 'red' }}>{error}</div>
          ) : (
            <Routes>
              <Route
                path="/registros"
                element={<Dashboard registros={registros} onReload={cargarDatos} />}
              />
              <Route path="/graficos" element={<Graficos registros={registros} />} />
              <Route path="/reportes" element={<Reportes registros={registros} />} />
              <Route path="/config-reportes" element={<ConfigReportes />} />
              {/* Solo visible para admin: Panel de Control */}
              {localStorage.getItem('rol') === 'admin' && (
                <Route path="/panel-control" element={<PanelControl />} />
              )}
              <Route
                path="/"
                element={<Dashboard registros={registros} onReload={cargarDatos} />}
              />
            </Routes>
          )}
        </div>
        <div
          style={{
            padding: 16,
            textAlign: 'center',
            borderTop: '1px solid rgba(255,255,255,0.1)',
            backgroundColor: 'rgba(0,0,0,0.2)',
            color: 'rgba(255,255,255,0.7)',
            fontSize: 13,
            fontWeight: 500,
            fontFamily: 'Roboto, Helvetica Neue, Arial, sans-serif',
            letterSpacing: 0.1,
          }}
        >
          Derechos reservados Servicios Estratégicos de Protección y Vigilancia Integral S. A.
          (Seprovinsa Security).
        </div>
      </div>
      {isMobile && <Sidebar />}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <RegistrosProvider>
        <Router>
          <AppRoutes />
        </Router>
      </RegistrosProvider>
    </AuthProvider>
  );
};

export default App;
